<script setup lang="tsx">
import IconButton from '../shared/IconButton.vue'
import PlusButton from '../widgets/PlusButton.vue'
import { hasPermission, hasPermissionOwn, isRowClick } from '@/services/utils'
import useRiskColumns from './columns'
import type { Asset, Responsible, Risk, RiskMeta } from '@/models/api-generated'
import MetaRow from './MetaRow.vue'
import RiskDescriptionCard from './cards/RiskDescriptionCard.vue'
import { computed, onMounted, ref, watch } from 'vue'
import { defaultRiskMeta } from './shared'
import AssetSelect from './AssetSelect.vue'
import ThreatsCard from './cards/ThreatsCard.vue'
import VulnerabilitiesCard from './cards/VulnerabilitiesCard.vue'
import RiskCategorySelect from './matrix/RiskCategorySelect.vue'
import RiskCatalogSelect from './matrix/RiskCatalogSelect.vue'
import { RT_Exclude, PermAllRiskWrite, PermOwnRiskWrite, PermRiskAdd } from '@/models/enums-generated'
import { riskConfig } from './riskConfig'
import ScoreDot from './ScoreDot.vue'

const props = withDefaults(
	defineProps<{
		class?: any
		component?: string
		isEdit: boolean
		onRiskDelete: (arg0: number) => void
	}>(),
	{
		component: 'risks',
	},
)

const model = defineModel<Risk>({ required: true })
const { columns } = useRiskColumns(props.component)
const deletedMetaIds = defineModel<Set<number>>('deletedMetaIds', { required: true })
const defaultRiskConnection = computed(() => model.value.riskMetas.find((m) => m.asset?.id === 0) ?? model.value.riskMetas[0])
const originalResponsibles = ref<Map<number, Responsible | undefined>>(new Map())
const canAddRisks = computed(() => hasPermission(PermRiskAdd))

onMounted(() => {
	model.value.riskMetas.forEach((meta) => {
		if (meta.id) {
			originalResponsibles.value.set(meta.id, meta.responsible)
		}
	})
})

const canEditRiskFields = computed(() => {
	if (hasPermission(PermAllRiskWrite) || !model.value.id) return true

	return model.value.riskMetas.some((meta) => {
		const currentResponsible = meta.responsible
		const originalResponsible = meta.id ? originalResponsibles.value.get(meta.id) : undefined

		const canEditCurrent = hasPermissionOwn(currentResponsible, PermAllRiskWrite, PermOwnRiskWrite)
		const canEditOriginal = originalResponsible ? hasPermissionOwn(originalResponsible, PermAllRiskWrite, PermOwnRiskWrite) : false

		return canEditCurrent || canEditOriginal
	})
})

const usedAssetIds = computed(() => {
	if (!model.value) return []
	return model.value.riskMetas.map((meta) => meta.asset?.id).filter((id) => id && id > 0)
})

watch(
	defaultRiskConnection,
	(meta) => {
		if (!meta) return

		const metas = model.value.riskMetas
		const idx = metas.indexOf(meta)
		if (idx > 0) {
			metas.splice(idx, 1)
			metas.unshift(meta)
		}

		const defaultThreats = meta.threats ?? []
		const defaultVulns = meta.vulnerabilities ?? []

		metas.forEach((m) => {
			if (m === meta) return

			m.catalogMeta = meta.catalogMeta
			m.category = meta.category
			m.confidentiality = meta.confidentiality
			m.integrity = meta.integrity
			m.availability = meta.availability

			const defaultThreatIds = new Set(defaultThreats.map((t) => t.id))
			const currentThreatIds = new Set(m.threats.map((t) => t.id))

			defaultThreats.forEach((t) => {
				if (!currentThreatIds.has(t.id)) m.threats.push(t)
			})

			m.threats = m.threats.filter((t) => defaultThreatIds.has(t.id))

			const defaultVulnIds = new Set(defaultVulns.map((v) => v.id))
			const currentVulnIds = new Set(m.vulnerabilities.map((v) => v.id))

			defaultVulns.forEach((v) => {
				if (!currentVulnIds.has(v.id)) m.vulnerabilities.push(v)
			})

			m.vulnerabilities = m.vulnerabilities.filter((v) => defaultVulnIds.has(v.id))
		})
	},
	{ deep: true },
)

watch(
	() => model.value.isExpanded,
	(isExpanded) => {
		model.value.riskMetas.forEach((meta) => {
			meta.isExpanded = isExpanded
		})
	},
)

function rowClick(e: MouseEvent) {
	if (!isRowClick(e)) return
	model.value.isExpanded = !model.value.isExpanded
}

const showAssetSelector = ref(false)
const selectedAsset = ref<Asset>()

function openAssetSelector() {
	showAssetSelector.value = true
}

function addMetaFromAsset(asset: Asset) {
	if (!asset) return
	model.value.riskMetas.push(defaultRiskMeta(asset, model.value.riskMetas[0]))
	showAssetSelector.value = false
}

function isAssessed(meta: RiskMeta) {
	return meta.probability !== undefined && meta.impact !== undefined
}

function isExcluded(meta: RiskMeta) {
	return meta.treatment === RT_Exclude
}

const validMetas = computed(() => {
	return model.value.riskMetas.filter((m) => isAssessed(m) && !isExcluded(m))
})

const averageScore = computed(() => {
	if (!validMetas.value.length) return 0
	return validMetas.value.reduce((sum, m) => sum + (m.score ?? 0), 0) / validMetas.value.length
})

const averageResidual = computed(() => {
	if (!validMetas.value.length) return 0
	return validMetas.value.reduce((sum, m) => sum + (m.residual ?? 0), 0) / validMetas.value.length
})

function handleMetaDelete(index: number, metaId?: number) {
	if (!metaId) {
		model.value.riskMetas.splice(index, 1)
	} else {
		if (deletedMetaIds.value.has(metaId)) {
			deletedMetaIds.value.delete(metaId)
		} else {
			deletedMetaIds.value.add(metaId)
		}
	}
}

function calculateDynamicWidth(items: Array<{ title?: string }>, baseWidth = 3, charWidth = 0.5, minChars = 8) {
	if (!riskConfig.value) return '10rem'
	const maxLength = Math.max(...items.map((item) => item.title?.length || 0), minChars)
	return `${baseWidth + maxLength * charWidth}rem`
}

const cssVars = computed(() => ({
	'--probability-axis-width': calculateDynamicWidth(riskConfig.value?.riskProbabilities || []),
	'--impact-axis-width': calculateDynamicWidth(riskConfig.value?.riskImpacts || []),
	'--level-axis-width': calculateDynamicWidth(riskConfig.value?.riskLevels || [], 3, 0.4),
}))
</script>

<template>
	<tr :class="[props.class, 'risk', 'odd']" @click="rowClick">
		<td :data-test="'expand-risk-' + model.id" class="text-center">
			<PlusButton v-model="model.isExpanded"></PlusButton>
		</td>
		<template v-if="isEdit && model.isCustom && canEditRiskFields">
			<td v-if="columns.code.value.isVisible">
				<input
					:data-test="'risk-code-input-' + model.id"
					:class="['form-control', { 'is-invalid': !model.code.length }]"
					v-model="model.code"
				/>
			</td>
			<td v-if="columns.name.value.isVisible">
				<input
					:data-test="'risk-name-input-' + model.id"
					:class="['form-control', { 'is-invalid': !model.name.length }]"
					v-model="model.name"
				/>
			</td>
			<td v-if="columns.catalog.value.isVisible && defaultRiskConnection">
				<RiskCatalogSelect
					:data-test="'risk-catalog-input' + defaultRiskConnection.id"
					:multi-select="false"
					v-model="defaultRiskConnection.catalogMeta"
					:allow-empty="false"
					:class="{ 'is-invalid': !defaultRiskConnection.catalogMeta.id }"
				/>
			</td>
		</template>
		<template v-else>
			<td v-if="columns.code.value.isVisible">{{ model.code }}</td>
			<td v-if="columns.name.value.isVisible">{{ model.name }}</td>
			<td v-if="columns.catalog.value.isVisible && defaultRiskConnection">{{ defaultRiskConnection.catalogMeta.catalog.name }}</td>
		</template>
		<template v-if="defaultRiskConnection">
			<td v-if="columns.category.value.isVisible">
				<template v-if="isEdit && canEditRiskFields">
					<RiskCategorySelect
						v-model="defaultRiskConnection.category"
						data-test="risk-category-input"
						:exclude-ids="defaultRiskConnection.category?.id ? [defaultRiskConnection.category.id] : []"
					/>
				</template>
				<template v-else>{{ defaultRiskConnection.category?.name }}</template>
			</td>

			<td v-if="columns.cia.value.isVisible">
				<template v-if="isEdit && canEditRiskFields">
					<div class="d-flex justify-content-center gap-2">
						<div class="d-flex flex-column align-items-center">
							<span>C</span>
							<input type="checkbox" class="form-check-input" v-model="defaultRiskConnection.confidentiality" />
						</div>
						<div class="d-flex flex-column align-items-center">
							<span>I</span>
							<input type="checkbox" class="form-check-input" v-model="defaultRiskConnection.integrity" />
						</div>
						<div class="d-flex flex-column align-items-center">
							<span>A</span>
							<input type="checkbox" class="form-check-input" v-model="defaultRiskConnection.availability" />
						</div>
					</div>
				</template>
				<template v-else>
					<span class="text-nowrap">
						{{ defaultRiskConnection.confidentiality ? 'C' : '-' }}
						{{ defaultRiskConnection.integrity ? 'I' : '-' }}
						{{ defaultRiskConnection.availability ? 'A' : '-' }}
					</span>
				</template>
			</td>
		</template>

		<td v-if="columns.connections.value.isVisible" class="text-center">
			{{ model.riskMetas.length }}
		</td>
		<td v-if="columns.score.value.isVisible">
			<ScoreDot v-if="validMetas.length" v-model="averageScore" />
		</td>
		<td v-if="columns.residual.value.isVisible">
			<ScoreDot v-if="validMetas.length" v-model="averageResidual" />
		</td>
		<td v-if="isEdit" class="w-auto-32 text-center">
			<IconButton
				v-if="!model.id"
				:data-test="'risk-delete-' + model.id"
				class="bi bi-trash text-danger"
				@click="onRiskDelete"
				:title="$t('misc.delete')"
				size="fs-5"
			/>
		</td>
	</tr>

	<template v-if="model.isExpanded">
		<tr>
			<td colspan="100%" class="pt-4">
				<RiskDescriptionCard
					:data-test="'risk-description-' + model.id"
					:is-edit="isEdit && canEditRiskFields"
					:is-custom="model.isCustom"
					v-model="model.description"
				/>
			</td>
		</tr>

		<tr v-if="defaultRiskConnection" class="my-4">
			<td colspan="100%">
				<div class="row gx-3">
					<div class="col d-flex">
						<VulnerabilitiesCard
							v-model="defaultRiskConnection.vulnerabilities"
							:is-edit="isEdit && canEditRiskFields"
							:risk-meta-id="defaultRiskConnection.id"
						></VulnerabilitiesCard>
					</div>

					<div class="col d-flex">
						<ThreatsCard
							v-model="defaultRiskConnection.threats"
							:is-edit="isEdit && canEditRiskFields"
							:risk-meta-id="defaultRiskConnection.id"
						></ThreatsCard>
					</div>
				</div>
			</td>
		</tr>
	</template>
	<tr v-if="canAddRisks">
		<td v-if="isEdit" colspan="100% d-flex align-items-center">
			<button
				:data-test="'add-new-risk-meta-' + model.id"
				type="button"
				class="btn btn-outline-primary btn-sm"
				@click="openAssetSelector()"
			>
				<i class="bi bi-plus-circle me-1"></i> {{ $t('risk.addNewMeta') }}
			</button>
			<AssetSelect
				v-if="showAssetSelector"
				v-model="selectedAsset"
				@select="addMetaFromAsset"
				@close="showAssetSelector = false"
				:exclude-ids="usedAssetIds"
			/>
		</td>
	</tr>
	<tr>
		<td colspan="100%" class="ps-4">
			<div class="table-responsive">
				<table class="table mb-0 subtable" :class="{ 'edit-mode': isEdit }" :style="cssVars">
					<thead>
						<tr>
							<th class="col-expand"></th>
							<th v-if="columns.connections.value.isVisible" class="col-connection text-center">
								<i class="bi bi-link-45deg" v-tooltip="columns.connections.value.label"></i>
							</th>
							<th v-if="columns.probability.value.isVisible" class="col-probability text-center">
								{{ columns.probability.value.label }}
							</th>
							<th v-if="columns.impact.value.isVisible" class="col-impact text-center">
								{{ columns.impact.value.label }}
							</th>
							<th v-if="columns.score.value.isVisible" class="col-score text-center">
								{{ columns.score.value.label }}
							</th>
							<th v-if="columns.acceptedResidualRisk.value.isVisible" class="col-accepted text-center">
								<i class="bi bi-patch-check" v-tooltip="columns.acceptedResidualRisk.value.label"></i>
							</th>
							<th v-if="columns.residual.value.isVisible" class="col-residual">
								{{ columns.residual.value.label }}
							</th>
							<th v-if="columns.mitigationMeasures.value.isVisible" class="col-mitigation text-center">
								<i class="bi bi-shield-check" v-tooltip="columns.mitigationMeasures.value.label"></i>
							</th>
							<th v-if="columns.responsible.value.isVisible" class="col-responsible text-center">
								<i class="bi bi-person-circle" v-tooltip="columns.responsible.value.label"></i>
							</th>
							<th v-if="columns.treatment.value.isVisible" class="col-treatment text-center">
								<i class="bi bi-bandaid" v-tooltip="columns.treatment.value.label"></i>
							</th>
							<th v-if="columns.dueDate.value.isVisible" class="col-duedate text-center">
								<i class="bi bi-clock" v-tooltip="columns.dueDate.value.label"></i>
							</th>
							<th v-if="columns.lastReview.value.isVisible" class="col-review text-center">
								<i class="bi bi-calendar-check" v-tooltip="columns.lastReview.value.label"></i>
							</th>
							<th class="col-history text-center">
								<i class="bi bi-clock-history"></i>
							</th>
							<th v-if="isEdit" class="col-delete"></th>
						</tr>
					</thead>

					<tbody>
						<MetaRow
							v-for="(r, i) in model.riskMetas"
							:key="r.id"
							v-model="model.riskMetas[i]"
							:is-edit="props.isEdit || !r.id"
							:risk-name="model.name"
							:is-deleted="r.id ? deletedMetaIds.has(r.id) : false"
							:on-meta-delete="() => handleMetaDelete(i, r.id)"
						/>
					</tbody>
				</table>
			</div>
		</td>
	</tr>
</template>

<style lang="sass" scoped>
@import '~bootstrap/scss/functions'
@import '~bootstrap/scss/variables'

tr.submethod > td
  cursor: pointer
  white-space: nowrap

tr.group-row > td
  cursor: default

td
  text-overflow: ellipsis

tr td
  border-bottom: none

tr.is-changed > td
  background-color: #eeeeff

tr.is-deleted > td
  background-color: #ffeeee

.smaller-text
  font-size: 0.5rem

:deep(.multiselect.is-invalid .multiselect__tags)
  border: 1px solid #dc3545
  border-radius: 0.25rem

.subtable
  table-layout: fixed
  width: 100%

  th, td
    overflow: hidden
    text-overflow: ellipsis
    white-space: nowrap
    padding: 0.5rem

  .col-expand
    width: 2rem
  .col-connection
    width: 15rem
  .col-probability
    width: var(--probability-axis-width)
  .col-impact
    width: var(--impact-axis-width)
  .col-score
    width: var(--level-axis-width)
  .col-accepted
    width: 4rem
  .col-residual
    width: var(--level-axis-width)
  .col-mitigation
    width: 6rem
  .col-responsible
    width: 8rem
  .col-treatment
    width: 8rem
  .col-duedate
    width: 6rem
  .col-review
    width: 6rem
  .col-history
    width: 2rem
  .col-delete
    width: 2rem

  &.edit-mode
    .col-probability
      width: calc(var(--probability-axis-width) + 3rem)
    .col-impact
      width: calc(var(--impact-axis-width) + 3rem)
    .col-accepted
      width: 10rem
    .col-responsible
      width: 16rem
    .col-treatment
      width: 10rem
    .col-review
      width: 8rem
    .col-duedate
      width: 10rem
</style>
